import React, { useState, useEffect, useCallback } from 'react';
import { financeApi } from '../services/financeApi';

export const EmployeePerformance = ({ data: initialData }) => {
    const [data, setData] = useState(initialData);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [sortBy, setSortBy] = useState('collection_stats.total_amount');
    const [sortOrder, setSortOrder] = useState('desc');
    const [dateFilter, setDateFilter] = useState({
        start_date: '',
        end_date: ''
    });
    const [tempDateFilter, setTempDateFilter] = useState({
        start_date: '',
        end_date: ''
    });

    // Debounced filter function
    const debounceTimer = React.useRef(null);

    const fetchFilteredData = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);

            const filters = {
                startDate: dateFilter.start_date || undefined,
                endDate: dateFilter.end_date || undefined,
                topN: 50, // Get more results for filtering
                ordering: sortOrder === 'desc' ? `-${sortBy}` : sortBy
            };

            // Remove undefined values
            Object.keys(filters).forEach(key => filters[key] === undefined && delete filters[key]);

            const response = await financeApi.getEmployeePerformance(filters);
            setData(response);
        } catch (err) {
           
            console.error('Error fetching employee performance:', err);
            setError('Failed to load employee performance data');
        } finally {
            setLoading(false);
        }
    }, [dateFilter, sortBy, sortOrder]);

    // Debounced API call for date filters
    const debouncedFetchData = useCallback(() => {
        if (debounceTimer.current) {
            clearTimeout(debounceTimer.current);
        }
        debounceTimer.current = setTimeout(() => {
            fetchFilteredData();
        }, 1000); // 1 second debounce to allow for date selection
    }, [fetchFilteredData]);

    // Initial load and when data prop changes
    useEffect(() => {
        if (initialData) {
            setData(initialData);
            // Set default date range from API data
            if (initialData.date_range) {
                const defaultDates = {
                    start_date: initialData.date_range.start_date,
                    end_date: initialData.date_range.end_date
                };
                setDateFilter(defaultDates);
                setTempDateFilter(defaultDates);
            }
        }
    }, [initialData]);

    // Only fetch data when dateFilter changes (not tempDateFilter)
    useEffect(() => {
        // Check if dates are different from initial data
        const hasDateChange = initialData?.date_range && (
            dateFilter.start_date !== initialData.date_range.start_date ||
            dateFilter.end_date !== initialData.date_range.end_date
        );

        if (hasDateChange && (dateFilter.start_date || dateFilter.end_date)) {
            debouncedFetchData();
        }
    }, [dateFilter, debouncedFetchData, initialData]);

    // Clean up debounce timer
    useEffect(() => {
        return () => {
            if (debounceTimer.current) {
                clearTimeout(debounceTimer.current);
            }
        };
    }, []);

    if (!data && !loading) {
        return (
            <div className="section-card">
                <h2>Employee Summary</h2>
                <div className="loading-placeholder">Loading...</div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="section-card">
                <h2>Employee Summary</h2>
                <div className="error-message">
                    <p>{error}</p>
                    <button onClick={fetchFilteredData} className="retry-btn">
                        Retry
                    </button>
                </div>
            </div>
        );
    }

    const { date_range, performance_summary, top_performers } = data || {};

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        }).format(amount);
    };

    const formatDate = (dateStr) => {
        return new Date(dateStr).toLocaleDateString('en-IN', {
            month: 'short',
            day: 'numeric'
        });
    };

    // Since we're now filtering on the backend, we can directly use the performers
    const filteredPerformers = top_performers || [];

    const handleSort = (field) => {
        if (sortBy === field) {
            setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
        } else {
            setSortBy(field);
            setSortOrder('desc');
        }
        // This will trigger a new API call via useEffect
    };

    // Note: Sorting is now handled on the backend, so we don't need client-side sorting
    const sortedPerformers = filteredPerformers;

    const handleFilterChange = (filterType, value) => {
        // Update temporary filter immediately for UI responsiveness
        switch (filterType) {
            case 'start_date':
                setTempDateFilter(prev => ({ ...prev, start_date: value }));
                break;
            case 'end_date':
                setTempDateFilter(prev => ({ ...prev, end_date: value }));
                break;
            default:
                break;
        }
    };

    // Handle date input blur or Enter key to commit the date change
    const handleDateCommit = (filterType, value) => {
        switch (filterType) {
            case 'start_date':
                setDateFilter(prev => ({ ...prev, start_date: value }));
                break;
            case 'end_date':
                setDateFilter(prev => ({ ...prev, end_date: value }));
                break;
            default:
                break;
        }
    };

    const clearFilters = () => {
        if (initialData?.date_range) {
            const defaultDates = {
                start_date: initialData.date_range.start_date,
                end_date: initialData.date_range.end_date
            };
            setDateFilter(defaultDates);
            setTempDateFilter(defaultDates);
        } else {
            setDateFilter({ start_date: '', end_date: '' });
            setTempDateFilter({ start_date: '', end_date: '' });
        }
        // This will trigger a reset to original data
    };

    return (
        <div className="section-card">
            <div className="section-header">
                <h2>Employee Summary</h2>
                <div className="performance-summary">
                    <span className="summary-item">
                        Period: {formatDate(date_range.start_date)} - {formatDate(date_range.end_date)}
                    </span>
                    <span className="summary-item">
                        Total Employees: {performance_summary.total_employees}
                    </span>
                    <span className="summary-item">
                        Showing: {filteredPerformers.length} employees
                    </span>
                </div>
            </div>

            {/* Filters Section */}
            <div className="performance-filters">
                <div className="filter-row">
                    <div className="filter-group">
                        <label>Start Date:</label>
                        <input
                            type="date"
                            value={tempDateFilter.start_date}
                            onChange={(e) => handleFilterChange('start_date', e.target.value)}
                            onBlur={(e) => handleDateCommit('start_date', e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    handleDateCommit('start_date', e.target.value);
                                }
                            }}
                            className="filter-input"
                            disabled={loading}
                        />
                    </div>
                    <div className="filter-group">
                        <label>End Date:</label>
                        <input
                            type="date"
                            value={tempDateFilter.end_date}
                            onChange={(e) => handleFilterChange('end_date', e.target.value)}
                            onBlur={(e) => handleDateCommit('end_date', e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    handleDateCommit('end_date', e.target.value);
                                }
                            }}
                            className="filter-input"
                            disabled={loading}
                        />
                    </div>
                    <div className="filter-group">
                        <label>&nbsp;</label>
                        <button
                            onClick={() => {
                                setDateFilter({ ...tempDateFilter });
                            }}
                            className={`apply-filters-btn ${
                                JSON.stringify(dateFilter) !== JSON.stringify(tempDateFilter)
                                    ? 'has-changes'
                                    : ''
                            }`}
                            title="Apply date filters"
                            disabled={loading}
                        >
                            {loading ? 'Loading...' :
                             JSON.stringify(dateFilter) !== JSON.stringify(tempDateFilter)
                                ? 'Apply Changes'
                                : 'Apply Filters'}
                        </button>
                    </div>
                    <div className="filter-group">
                        <label>&nbsp;</label>
                        <button
                            onClick={clearFilters}
                            className="clear-filters-btn"
                            title="Reset to default date range"
                            disabled={loading}
                        >
                            Reset Dates
                        </button>
                    </div>
                </div>
                {loading && (
                    <div className="filter-loading">
                        <span>🔄 Applying filters...</span>
                    </div>
                )}
            </div>

            <div className="performance-table-container">
                {loading && (
                    <div className="table-loading-overlay">
                        <div className="loading-spinner">Loading filtered data...</div>
                    </div>
                )}
                <table className="performance-table">
                    <thead>
                        <tr>
                            <th
                                onClick={() => handleSort('employee.name')}
                                className={`sortable ${sortBy === 'employee.name' ? sortOrder : ''}`}
                            >
                                Employee
                            </th>
                            <th>Department</th>
                            <th
                                onClick={() => handleSort('collection_stats.total_amount')}
                                // className={`sortable ${sortBy === 'collection_stats.total_amount' ? sortOrder : ''}`}
                            >
                                Collections
                            </th>
                            <th>Transfers</th>
                            <th>Deposits</th>
                            <th>Reconciliations</th>
                        </tr>
                    </thead>
                    <tbody>
                        {sortedPerformers.map((emp) => {
                            return (
                                <tr key={emp.user_id} className="performance-row">
                                    <td>
                                        <div className="employee-info">
                                            <strong>{emp.employee.name}</strong>
                                            <small>({emp.employee.unique_code})</small>
                                            <div className="contact">{emp.mobile}</div>
                                        </div>
                                    </td>
                                    <td>
                                        <div className="department-info">
                                            <span>{emp.employee.department}</span>
                                            <small>{emp.employee.designation}</small>
                                        </div>
                                    </td>
                                    <td>
                                        <div className="collection-stats">
                                            <div className="amount">
                                                {formatCurrency(emp.collection_stats.total_amount)}
                                            </div>
                                            <small>{emp.collection_stats.total_count} transactions</small>
                                            <div className="breakdown">
                                                {emp.collection_stats.breakdown.cash.amount > 0 && (
                                                    <span className="method-tag cash">
                                                        Cash: {formatCurrency(emp.collection_stats.breakdown.cash.amount)}
                                                    </span>
                                                )}
                                                {emp.collection_stats.breakdown.cheque.amount > 0 && (
                                                    <span className="method-tag cheque">
                                                        Cheque: {formatCurrency(emp.collection_stats.breakdown.cheque.amount)}
                                                    </span>
                                                )}
                                                {emp.collection_stats.breakdown.dd.amount > 0 && (
                                                    <span className="method-tag dd">
                                                        DD: {formatCurrency(emp.collection_stats.breakdown.dd.amount)}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    </td>
                                    <td>
                                        <div className="collection-stats">
                                            <div className="amount">
                                                {formatCurrency(emp.action_stats.transfers_made.total_amount)}
                                            </div>
                                            <small>{emp.action_stats.transfers_made.total_count} transfers</small>
                                        </div>
                                    </td>
                                    <td>
                                        <div className="collection-stats">
                                            <div className="amount">
                                                {formatCurrency(emp.action_stats.deposits_made.total_amount)}
                                            </div>
                                            <small>{emp.action_stats.deposits_made.total_count} deposits</small>
                                        </div>
                                    </td>
                                    <td>
                                        <div className="collection-stats">
                                            <div className="amount">
                                                {formatCurrency(emp.action_stats.reconciliations_made.total_amount)}
                                            </div>
                                            <small>{emp.action_stats.reconciliations_made.total_count} reconciliations</small>
                                        </div>
                                    </td>
                                </tr>
                            );
                        })}

                        {/* Aggregate Row */}
                        {sortedPerformers.length > 0 && (
                            <tr className="performance-row aggregate-row">
                                <td>
                                    <div className="employee-info">
                                        <strong>Total</strong>
                                        <small>({sortedPerformers.length} employees)</small>
                                    </div>
                                </td>
                                <td>
                                    <div className="department-info">
                                        <span>All Departments</span>
                                    </div>
                                </td>
                                <td>
                                    <div className="collection-stats">
                                        <div className="amount">
                                            {formatCurrency(
                                                sortedPerformers.reduce((sum, emp) =>
                                                    sum + emp.collection_stats.total_amount, 0
                                                )
                                            )}
                                        </div>
                                        <small>
                                            {sortedPerformers.reduce((sum, emp) =>
                                                sum + emp.collection_stats.total_count, 0
                                            )} transactions
                                        </small>
                                        <div className="breakdown">
                                            {(() => {
                                                const totalCash = sortedPerformers.reduce((sum, emp) =>
                                                    sum + emp.collection_stats.breakdown.cash.amount, 0
                                                );
                                                const totalCheque = sortedPerformers.reduce((sum, emp) =>
                                                    sum + emp.collection_stats.breakdown.cheque.amount, 0
                                                );
                                                const totalDD = sortedPerformers.reduce((sum, emp) =>
                                                    sum + emp.collection_stats.breakdown.dd.amount, 0
                                                );
                                                return (
                                                    <>
                                                        {totalCash > 0 && (
                                                            <span className="method-tag cash">
                                                                Cash: {formatCurrency(totalCash)}
                                                            </span>
                                                        )}
                                                        {totalCheque > 0 && (
                                                            <span className="method-tag cheque">
                                                                Cheque: {formatCurrency(totalCheque)}
                                                            </span>
                                                        )}
                                                        {totalDD > 0 && (
                                                            <span className="method-tag dd">
                                                                DD: {formatCurrency(totalDD)}
                                                            </span>
                                                        )}
                                                    </>
                                                );
                                            })()}
                                        </div>
                                    </div>
                                </td>
                                <td>
                                    <div className="collection-stats">
                                        <div className="amount">
                                            {formatCurrency(
                                                sortedPerformers.reduce((sum, emp) =>
                                                    sum + emp.action_stats.transfers_made.total_amount, 0
                                                )
                                            )}
                                        </div>
                                        <small>
                                            {sortedPerformers.reduce((sum, emp) =>
                                                sum + emp.action_stats.transfers_made.total_count, 0
                                            )} transfers
                                        </small>
                                    </div>
                                </td>
                                <td>
                                    <div className="collection-stats">
                                        <div className="amount">
                                            {formatCurrency(
                                                sortedPerformers.reduce((sum, emp) =>
                                                    sum + emp.action_stats.deposits_made.total_amount, 0
                                                )
                                            )}
                                        </div>
                                        <small>
                                            {sortedPerformers.reduce((sum, emp) =>
                                                sum + emp.action_stats.deposits_made.total_count, 0
                                            )} deposits
                                        </small>
                                    </div>
                                </td>
                                <td>
                                    <div className="collection-stats">
                                        <div className="amount">
                                            {formatCurrency(
                                                sortedPerformers.reduce((sum, emp) =>
                                                    sum + emp.action_stats.reconciliations_made.total_amount, 0
                                                )
                                            )}
                                        </div>
                                        <small>
                                            {sortedPerformers.reduce((sum, emp) =>
                                                sum + emp.action_stats.reconciliations_made.total_count, 0
                                            )} reconciliations
                                        </small>
                                    </div>
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};
